import { Injectable, NgZone } from '@angular/core';
import { ICastService } from "@cast/interfaces/i-cast-service";
import { LayoutState, ViewStateRecordsAlias } from "@common/services/layout-state";
import { DataStore } from "@suite/common/data-models/data-store/data-store";
import { ServiceResponse } from "@common/data-models/service/service-response";
import { AppService } from "@suite/app.service";
import { SearchResults, TreeLikeFlatNode } from "@suite/common/data-models/search-results/search-results";
import { GroupValueHelpers, GroupKeyComparatorHelpers, RecordComparatorHelpers, ResultGroupingDefinition } from "@common/data-models/search-results/result-group";
import { PublicationViewStateRecord } from "@suite/common/data-models/records/publication-viewstate-record";
import { CastSearchService } from "@cast/services/cast-search-service";
import { VisualStateService } from "@common/services/visual-state-service";
import { NavigationService } from "@common/services/navigation-service";
import { ObjectTypes } from "@common/enums/object-types";
import { GlobalTablesService } from "@common/services/global-tables-service";
import { ViewStateTypes } from "@common/enums/viewstate-types";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { LanguageSettings } from "@common/services/language-settings";
import { ViewStateOptions } from "@common/data-models/navigation/viewstate-options";
import { ViewStateLayouts } from "@common/enums/viewstate-layouts";
import { CastSearchResults } from "@cast/data-models/cast-search-results/cast-search-results";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { FieldDef } from "@common/data-models/service/field-def";
import { PublicationRecord } from '../data-models/records/publication-record';
import { PublicationListRecord } from '../data-models/records/publication-list-record';
import { Record } from '@common/data-models/records/record';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { BasicContentService } from '@suite/common/services/basic-content-service';
import { ViewStateRecord } from "@common/data-models/records/viewstate-record";
import { ServiceCollectionRecord } from '@suite/common/data-models/service/service-collection-record';
import { ObjectProperties } from "@common/data-models/service/object-properties";
import { RecordFactory } from "@common/data-models/records/record-factory";
import { PublicationService } from '@cast/services/publication-service';
import { ModalService } from '@common/components/modal/modal.service';
import { ExternalActionResponse } from '@common/data-models/service/external-action';
import { ExternalActionsService } from '@common/services/external-actions-service';
import { Relationship } from '@common/data-models/complex-types/relationship';
import { PlaybookSearchResults } from '@suite/playbook/data-models/playbook-search-results/playbook-search-results';
import { INavigateEvent } from '@suite/common/interfaces/i-navigate-event';
import { CollectionRecord } from "@common/data-models/records/collection-record";
import moment from 'moment-timezone';
import { StFullScreenDialog } from '@src/suite/common/st-full-screen-dialog/st-full-screen-dialog';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TopicRecord } from '@src/suite/playbook/data-models/records/topic-record';
import { NavigationDirection } from '@src/suite/common/enums/navigation-direction';
import { AssignmentRecord } from '@src/suite/playbook/data-models/records/assignment-record';
import { take, takeUntil } from 'rxjs/operators';
import { TopicService } from '@src/suite/playbook/services/topic-service';
import { PlaybookSearchService } from '@src/suite/playbook/services/playbook-search-service';
import { SingleViewStateRecord } from '@src/suite/common/data-models/records/single-viewstate-record';
import { Util } from '@src/suite/common/utils/util';

@Injectable({ providedIn: 'root' })
export class CastService implements ICastService {

    // Drag & drop
    public appendOnly: boolean = false;
    public dragStarted: boolean = false;
    public draggedGuid: string = null;
    public droppedGuid: string = null;

    public isCreatePublicationPending: boolean = false;

    public channelSections: { [id: string]: Array<PublicationRecord> } = {};
    public deletedChannelSections: { [id: string]: Array<PublicationRecord> } = {};
    public dialogRef: MatDialogRef<StFullScreenDialog, any> = null;

    public availableRecords: Array<Record> = new Array<Record>();
    public filteredAvailableRecords: Array<Record> = new Array<Record>();
    public addedRecords: Set<string> = new Set<string>();

    constructor(
        public appService: AppService,
        public castSearchService: CastSearchService,
        public viewStateService: LayoutState,
        public visualStateService: VisualStateService,
        public languageSettings: LanguageSettings,
        public globalTablesService: GlobalTablesService,
        public nomWebServicesJson: NomWebServicesJson,
        public ngZone: NgZone,
        public navigationService: NavigationService,
        public basicContentService: BasicContentService,
        public publicationService: PublicationService,
        public modalService: ModalService,
        private externalActionsService: ExternalActionsService,
        protected dialog: MatDialog,
        private topicService: TopicService,
        private layoutState: LayoutState,
        public playbookSearchService: PlaybookSearchService
    ) {
        CastSearchResults.castService = this;
        //this.viewStateRecordSubscription = this.viewStateService.ViewStateRecordObservable.subscribe(this.onViewStateRecordChange.bind(this));
        //his.viewStateRecordPropertySubscription = this.viewStateService.ViewStateRecordPropertyObservable.subscribe(this.onViewStateRecordPropertyChange.bind(this));


        //Clipboard.copyViewToClipboardHandlers["playbook"] = this.copyViewToClipBoard.bind(this);
        //Clipboard.renderers["playbook"] = this.copyToClipboardRenderer.bind(this);

        this.globalTablesService.staticPropertyHandlers["PUBLICATION"] = this.getScheduleStaticProperties.bind(this);

        //this.globalTablesService.isPropertyForType[ObjectTypes.WIRE] = this.isTopicProperty.bind(this);

        //this.globalTablesService.populateDefaultValueCallbacks[ObjectTypes.WIRE] = this.populateDefaultValuesForTopic.bind(this);

        this.globalTablesService.setFieldDefCallbacks[ObjectTypes.PUBLICATION] = this.setFieldDefsForPublications.bind(this);

        this.globalTablesService.getGroupingOptions[ViewStateTypes.PUBLICATION] = this.getGroupingOptionsForPublications.bind(this);

        this.globalTablesService.getSortingOptions[ViewStateTypes.PUBLICATION] = this.getSortingOptionsForSchedule.bind(this);

        this.globalTablesService.getLayoutOptions[ViewStateTypes.PUBLICATION] = this.getLayoutOptionsForSchedule.bind(this);

        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.PUBLICATION] = this.getFiltersForPublications(ViewStateTypes.PUBLICATION).bind(this);
    }

    public viewStateGuid: string = "";

    private _viewStatesRecords: Array<ViewStateRecord> = [];
    public get playbookViewStateRecords(): Array<ViewStateRecord> {
        if (!this._viewStatesRecords || this._viewStatesRecords.length === 0) {
            this._viewStatesRecords = DataStore.getSingleViewStates("playbook");
        }
        return this._viewStatesRecords;
    }

    public async getSavedViewItems(vs?: ViewStateRecord) {
        this.playbookSearchResults.dispose();
        this.availableRecords = new Array<Record>();
        if (!vs && this.layoutState.NavigationViewStateRecord) {
            let guid = (<PublicationViewStateRecord>this.layoutState.NavigationViewStateRecord).viewStateGUID;
            if (!guid) {
                // Fallback to first item from dropdown
                guid = this.playbookViewStateRecords[0].guid;
            }
            vs = DataStore.byGuid<ViewStateRecord>(guid, "");
        }
        if (vs && this.layoutState.NavigationViewStateRecord) {
            const res = await this.playbookSearchService.getSearchResults(<ViewStateRecordsAlias>vs);
            if (res.status) {
                this.availableRecords = new Array<Record>();
                DataStore.registerSearchResults("castPlaybook", res.data);
                this.playbookSearchResults.rebuildGroups((<SingleViewStateRecord>vs).viewStateGrouping.Value, (<SingleViewStateRecord>vs).viewStateSorting.Value, (<SingleViewStateRecord>vs).viewStateSortOrder, (<SingleViewStateRecord>vs).viewStateGroupSortOrder);
                let groupByField = (<SingleViewStateRecord>vs).viewStateGrouping.Value;
                if (res.data.topicGroups[groupByField]) {
                    for (let item of res.data.topicGroups[groupByField].keys) {
                        for (let rec of res.data.topicGroups[groupByField].entries[item.value]) {
                            if (Util.getBooleanValFromString((<PublicationViewStateRecord>this.layoutState.NavigationViewStateRecord).viewStateShowTopics)) {
                                rec.isAdded = this.addedRecords.has(rec.guid)
                                this.availableRecords.push(rec);
                            }
                            if (vs.viewStateType != ViewStateTypes.SEARCH) {
                                if (Util.getBooleanValFromString((<PublicationViewStateRecord>this.layoutState.NavigationViewStateRecord).viewStateShowAssignments)) {
                                    for (let childRec of rec.getChildren()) {
                                        childRec.isAdded = this.addedRecords.has(childRec.guid)
                                        this.availableRecords.push(childRec);
                                    }
                                }
                            }
                        }
                    }
                } else {
                    for (let topic of res.data.topics) {
                        if (this.layoutState.NavigationViewStateRecord && Util.getBooleanValFromString((<PublicationViewStateRecord>this.layoutState.NavigationViewStateRecord).viewStateShowTopics)) {
                            topic.isAdded = this.addedRecords.has(topic.guid)
                            this.availableRecords.push(topic);
                        }
                    }
                }

                if (this.layoutState.NavigationViewStateRecord && Util.getBooleanValFromString((<PublicationViewStateRecord>this.layoutState.NavigationViewStateRecord).viewStateShowAssignments)) {
                    for (let assignment of res.data.assignments) {
                        assignment.isAdded = this.addedRecords.has(assignment.guid)
                        this.availableRecords.push(assignment);
                    }
                }
            }
            this.filteredAvailableRecords = this.getFilteredAvailableRecords();
        }
    }

    public rebuildChannelSections() {
        this.availableRecords.forEach(rec => {
            rec.isAdded = this.addedRecords.has(rec.guid);
        });
        this.filteredAvailableRecords = this.getFilteredAvailableRecords();
    }

    public getFilteredAvailableRecords(): Array<Record> {
        if ((<PublicationViewStateRecord>this.layoutState.NavigationViewStateRecord).viewStateRemoveAddedRecords) {
            let res = this.availableRecords.filter(rec => {
                return !rec.isAdded;
            });
            return res;
        }
        return this.availableRecords;
    }

    public getScheduleStaticProperties(): { [id: string]: boolean } {
        let ret: { [id: string]: boolean } = {};
        return ret;
    }

    public getSortingOptionsForSchedule(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = PSTFieldNames.PSTChannel;
        ret.Options = [
            { id: PSTFieldNames.PSTChannel, title: this.languageSettings.PSTWebGroupByChannelLabel }
        ];
        return ret;
    }

    private _groupByPublication: Array<{ id: string, title: string }> = [];
    public get groupByPublication(): Array<{ id: string, title: string }> {
        if ((this._groupByPublication.length == 0) && this.languageSettings) {
            this._groupByPublication = this.getGroupingOptionsForPublications().Options;
        }
        return this._groupByPublication;
    }

    private _groupByPublicationKeys: Array<string> = [];
    public get groupByPublicationKeys(): Array<string> {
        if ((this._groupByPublicationKeys.length == 0) && (this.groupByPublication.length > 0)) {
            this._groupByPublicationKeys = this.groupByPublication.map(item => item.id);
        }
        return this._groupByPublicationKeys;
    }

    public getGroupingOptionsForPublications(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = PSTFieldNames.PSTChannel;
        ret.Options = [
            { id: PSTFieldNames.PSTChannel, title: this.languageSettings.PSTWebGroupByChannelLabel }
        ];
        return ret;
    }

    public getLayoutOptionsForSchedule(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = ViewStateLayouts.CONDENSE;
        ret.Options = [
            { id: ViewStateLayouts.CAST_CARD, title: this.languageSettings.PSTWebViewCardLabel },
            { id: ViewStateLayouts.CONDENSE, title: this.languageSettings.PSTWebViewCondensedLabel }
        ];
        return ret;
    }

    public getFiltersForPublications(type: number): (item: FieldDef) => boolean {
        return (item: FieldDef) => {
            if (item.Briefable && (item["PublicationListProp"] == true)) {
                return true;
            }
            return false;
        }
    }

    public setFieldDefsForPublications(fieldDef: FieldDef) {
        if (fieldDef.PublicationProp) {
            this.calculatePublicationPropComparators(fieldDef)
        }
    }

    private calculatePublicationPropComparators(prop: FieldDef) {
        let shouldAdd: boolean = true;
        switch (prop.Validation.toLowerCase()) {
            case 'time':
            case 'date': {
                CastSearchResults.publicationGroup2Defs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.DateTime(prop.ID),
                    GroupKeyComparatorHelpers.Value(),
                    RecordComparatorHelpers.DateTime(prop.ID)
                );
                break;
            }
            default:
                shouldAdd = false;
        }
        if (prop.GroupableProp && shouldAdd) {
            this.groupByPublication.push({ id: prop.ID, title: prop.DefaultCaption });
        }
    }

    public activeCollectionPollerStarted: boolean = false;

    public get castSearchResults(): CastSearchResults {
        return DataStore.getSearchResults<CastSearchResults>("cast");
    }

    public get playbookSearchResults(): PlaybookSearchResults {
        return DataStore.getSearchResults<PlaybookSearchResults>("castPlaybook");
    }

    public async loadPublicationLists(vs: ViewStateRecordsAlias, channelGuids: Array<string>) {
        let res: CastSearchResults = await this.castSearchService.getPublicationListsOfRestrictedChannelType(vs, channelGuids);
        if (res) {
            this.castSearchResults.publicationListsOfRestrictedChannelTypes = [...res.publicationListsOfRestrictedChannelTypes];
        } else {
            // Error
            this.nomWebServicesJson.showErrorModal({
                message: "Unable to get Cast Publication Lists search results",
                detailMessage: "Unable to get Cast search results. \n\rStackTrace: CastService.loadPublicationLists();"
            });
        }
    }

    onTopicOpenHandler(record?: TopicRecord, idx?: number, node?: TreeLikeFlatNode<Record>) {
        this.dialog.closeAll();
        this.dialog.afterAllClosed.pipe(take(1)).subscribe(() => {
            this.dialogRef = this.dialog.open(StFullScreenDialog, {
                hasBackdrop: true,
                disableClose: false,
                panelClass: 'topic-full-screen-panel',
                position: {},
                data: { data: record, index: idx }
            });
            this.dialogRef.afterOpened().pipe(take(1)).subscribe(() => {
                this.dialogRef.componentInstance.openTopic(record as TopicRecord, null, node);
                for (let i = 0; i < this.castSearchResults.treeControl.dataNodes.length; i++) {
                    if (this.castSearchResults.treeControl.dataNodes[i]?.object?.guid === node?.object?.guid && i >= idx) {
                        break;
                    }

                    if (this.castSearchResults.treeControl.dataNodes[i]?.level === 1) {
                        this.dialogRef.componentInstance.indexInData++;
                    }
                }
                this.dialogRef.componentInstance.indexInData = idx;
                this.dialogRef.componentInstance.totalCount = this.castSearchResults.treeControl.dataNodes.filter(n => n.level === 1)?.length || 1;
            });
            this.dialogRef.componentInstance.navigateTo.pipe(
                takeUntil(this.dialogRef.componentInstance.destroy)
            ).subscribe((event) => {
                this.navigateToTopic(event, this.dialogRef);
            });
        })
    }

    async onAssignmentOpenHandler(record?: AssignmentRecord, idx?: number, node?: TreeLikeFlatNode<Record>) {
        this.dialog.closeAll();
        let currentIdx = 0;
        for (let i = idx; i > 0; i--) {
            if (this.castSearchResults.treeControl.dataNodes[i].isMetadata) {
                currentIdx = i;
                break;
            }
        }
        this.dialog.afterAllClosed.pipe(take(1)).subscribe(() => {
            this.dialogRef = this.dialog.open(StFullScreenDialog, {
                hasBackdrop: true,
                disableClose: false,
                panelClass: 'topic-full-screen-panel',
                data: { data: record, index: currentIdx }
            });

            this.dialogRef.afterOpened().pipe(take(1)).subscribe(() => {
                this.dialogRef.componentInstance.openAssignment(record, node);
                for (let i = 0; i < this.castSearchResults.treeControl.dataNodes.length; i++) {
                    if (this.castSearchResults.treeControl.dataNodes[i]?.object?.guid === node?.object?.guid && i >= idx) {
                        break;
                    }
                    if (this.castSearchResults.treeControl.dataNodes[i]?.level === 1) {
                        this.dialogRef.componentInstance.indexInData++;
                    }

                }
                this.dialogRef.componentInstance.totalCount = this.castSearchResults.treeControl.dataNodes.filter(n => n.level === 1)?.length || 1;
            });

            this.dialogRef.componentInstance.navigateTo.pipe(
                takeUntil(this.dialogRef.componentInstance.destroy)
            ).subscribe((event) => {
                this.navigateToTopic(event, this.dialogRef);
            });
        })
    }

    private _loadPublicationListPendingRequests: number = 0;
    public async loadPublicationList(vs: ViewStateRecordsAlias, availableRecords?: Array<Record>) {
        if (!vs) vs = this.viewStateService.ViewStateRecord;
        if (!(<PublicationViewStateRecord>vs).publicationChannel) {
            return;
        }

        if ((<PublicationViewStateRecord>vs).publicationChannel.channelSchedule.toUpperCase() != "DAILY") {
            let modalRef = this.modalService.openAlert(this.languageSettings.PSTWebChannelScheduleNotSupportedLabel, this.languageSettings.PSTWebChannelScheduleNotSupportedDetailLabel + (<PublicationViewStateRecord>vs).publicationChannel.channelSchedule, "OK");
            //TODO: UNCOMMENT AFTER MODAL REWORK
            await modalRef.result;
            return new Promise<ServiceResponse<SearchResults>>(resolve => { resolve(new ServiceResponse<SearchResults>(false, null)); });
        }
        if (this._loadPublicationListPendingRequests == 0) {
            if (this.navigationService.currentListView) this.navigationService.currentListView.showLoader();
        }
        this._loadPublicationListPendingRequests++;
        await this.appService.ngZone.runOutsideAngular(async () => {
            //this.appService.LoadingTopicsSource.next(true);
            DataStore.disposeSearchResults();
            if (SearchResults.newlyAddedItems) {
                SearchResults.newlyAddedItems.dispose();
            }
            if (availableRecords) {
                for (let rec of availableRecords) {
                    if (rec.isCollection) {
                        DataStore.setCollection(<CollectionRecord>rec, true);
                    } else {
                        DataStore.set(rec);
                    }
                }
            }
            let res: ServiceResponse<SearchResults> = await this.castSearchService.getSearchResults(vs);

            if (!this.activeCollectionPollerStarted) {
                this.activeCollectionPollerStarted = true;
                //this.startActiveCollectionPoller();
            }
            if (res.status) {
                this.castSearchResults.publicationList = null;
                DataStore.registerSearchResults("cast", res.data);
                //this.castSearchResults.rebuildGroups((<SingleViewStateRecord>vs).viewStateGrouping.Value, (<SingleViewStateRecord>vs).viewStateSorting.Value, (<PublicationViewStateRecord>vs).viewStateSortOrder, (<PublicationViewStateRecord>vs).viewStateGroupSortOrder);
            } else {
                // Error
                this.nomWebServicesJson.showErrorModal({
                    message: "Unable to get Cast search results",
                    detailMessage: "Unable to get Cast search results. \n\rStackTrace: CastService.loadPublicationList();"
                });
            }
            this.appService.LoadingDataSource.next(false);
            this.appService.timeoutQueue.setTimeout(async () => {
                this._loadPublicationListPendingRequests--;
                this.appService.ngZone.run(() => {
                    this.navigationService.currentListChangeDetectorRef.detectChanges();
                });
                if (this._loadPublicationListPendingRequests == 0) {
                    if (!this.navigationService.CurrentNavigationItem || !this.navigationService.CurrentNavigationItem.isNewView()) {
                        this.navigationService.currentListView.hideLoader();
                    }
                }

                this.appService.ngZone.run(() => {
                    this.navigationService.currentListChangeDetectorRef.detectChanges();
                });

                if (this.navigationService.CurrentNavigationItem && this.navigationService.CurrentNavigationItem.isNewView()) {
                    await this.navigationService.saveCurrentNavigationItem();
                    if (this._loadPublicationListPendingRequests == 0) {
                        this.navigationService.currentListView.hideLoaderDirectly();
                    }
                }
            }, 1000);
        });
    }

    //public startActiveCollectionPoller() {
    //    this.ngZone.runOutsideAngular(async () => {
    //        setInterval(async () => {
    //            if (!this.nomWebServicesJson.noSession) {
    //                let vs: ViewStateRecordsAlias = this.viewStateService.ViewStateRecord;
    //                if (vs instanceof SingleViewStateRecord) {
    //                    let res = null;
    //                    //let res = await this.discoverSearchService.getFilteredResultsGuids(vs);
    //                    if (res && res.status) {
    //                        //DataStore.getSearchResults('discover').updateNewGuids(res.data, SearchResults.newlyAddedItems.itemsGuids);
    //                    } else {
    //                        this.nomWebServicesJson.noSession = true;
    //                    }
    //                }
    //            }
    //        }, this.appService.getSolrPollInvervalInSeconds());
    //    });
    //}

    public async deletePublication(publication: PublicationRecord): Promise<ServiceResponse<Record>> {
        if (publication && publication.path && publication.guid && publication.type) {
            let res: Promise<ServiceResponse<Record>> = this.basicContentService.deleteBasicContent(this.nomWebServicesJson.PSTServer, publication.path.fullPath, publication.guid, publication.type, null);
            await this.updatePublicationListModTime();
            return res;
        } else {
            return new ServiceResponse<Record>(false, "Unable to delete basic content: path, guid and type all need to be defined for the publication object.");
        }
    }

    //TODO: also needs to be changed such that a new NOM command is created and this only sends section and index to which to insert to
    // then nom manages the JSON
    // can insert or delete from index.
    public async updatePublicationListSequence(sequence: Array<string>,  sections: { [id: string]: Array<PublicationRecord> }): Promise<ServiceResponse<PublicationListRecord>> {
        let database: string = this.nomWebServicesJson.getDatabase();
        let path: string = this.castSearchResults.publicationList.path.fullPath;
        let collection: { [section: string]: Array<string> } = {};

        for (let key of sequence) {
            let pubs = sections[key];
            let guids: Array<string> = new Array<string>();
            if (pubs) {
                for (let i = 0; i < pubs.length; i++) {
                    if (pubs[i] && pubs[i].guid) {
                        guids.push(pubs[i].guid);
                    }
                }
            }
            collection[key] = guids;
        }

        let data = {
            "PublicationSequenceCollection": JSON.stringify(collection),
            "Type": ObjectTypes.PUBLICATIONLIST
        };

        let res = await this.basicContentService.updateBasicContent<PublicationListRecord>(database, path, this.castSearchResults.publicationList.guid, false, data, false, null);
        if (res.status) {
            for (let section in collection) {
                let guids: Array<string> = collection[section];
                if (guids.length > 0) {
                    let requestParams = {
                        action: 'update',
                        database: this.nomWebServicesJson.getDatabase(),
                        toSectionName: section,
                        toSectionIndex: -1,
                        collectionPath: this.castSearchResults.publicationList.path.fullPath,
                        collectionGuid: this.castSearchResults.publicationList.guid,
                        toRecordsToUpdateGuids: guids
                    };
                    try {
                        let response = await this.nomWebServicesJson.patch(
                            "/api/Publication",
                            JSON.stringify(requestParams), {
                            headers: new HttpHeaders({
                                "Content-Type": "application/json",
                                "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                            }),
                            responseType: 'json',
                            observe: 'response'
                        });
                    } catch (error) {
                        this.nomWebServicesJson.showErrorModal({
                            message: "Unable to update publication",
                            detailMessage: `Unable to update publication. \n\rStackTrace: CastService.createPublications(); \n\rError: ${error}`
                        });
                    }
                }
            }
        }
        return res;
    }

    public async createPublicationList(vs: ViewStateRecord, sections: Array<string>, channelSections?: { [id: string]: Array<PublicationRecord> }): Promise<ServiceResponse<PublicationListRecord>> {
        let response: HttpResponse<any> = null;
        let database: string = this.nomWebServicesJson.getDatabase();
        let path: string = "P_" + this.nomWebServicesJson.PSTGroup + "\\W\\" + this.nomWebServicesJson.PSTHoldFolder;
        let fields: { [id: string]: any } = {};
        const date = (<PublicationViewStateRecord>vs).publicationTime.get();

        fields["Type"] = ObjectTypes.PUBLICATIONLIST;

        let listTitle = (<PublicationViewStateRecord>vs).publicationListTitle ? (<PublicationViewStateRecord>vs).publicationListTitle : (<PublicationViewStateRecord>vs).title;
        fields["Title"] = listTitle + "-" + (<PublicationViewStateRecord>vs).publicationTime.moment.format("YYYY-MM-DD") + "-Revision" + ((<PublicationViewStateRecord>vs).publicationRevisionNumber + 1);
        fields["PSTPublicationChannelGUID"] = (<PublicationViewStateRecord>vs).publicationChannelGUID;
        fields["PSTPublicationListTitle"] = (<PublicationViewStateRecord>vs).publicationListTitle;
        fields["PSTPublicationListRevisionNumber"] = (<PublicationViewStateRecord>vs).publicationRevisionNumber;
        this.castSearchService.calculateNgTimeOffset(date);
        let vsPublicationTime = moment(date).format("YYYY-MM-DDTHH:mm:00.000") + "Z";
        fields["PSTPublicationListTime"] = vsPublicationTime;
        fields["PSTPublicationEditionName"] = (<PublicationViewStateRecord>vs).editionName;

        if (sections) {
            let sequence: { [section: string]: Array<string> } = {};
            for (let key of sections) {
                let pubs = channelSections[key];
                let guids: Array<string> = new Array<string>();
                if (pubs) {
                    for (let i = 0; i < pubs.length; i++) {
                        if (pubs[i] && pubs[i].guid) {
                            guids.push(pubs[i].guid);
                        }
                    }
                }
                sequence[key] = guids;
            }
            let sectionsSequence = {
                SectionsSequence: sections
            }

            fields["PublicationSequenceCollection"] = JSON.stringify({ ...sequence, ...sectionsSequence });
        }

        try {
            let requestParams = {
                database: database,
                path: path,
                dateTime: "",
                user: this.nomWebServicesJson.UserId,
                fieldsToUpdate: this.nomWebServicesJson.Convertor.toENPSFields(fields)
            }
            response = await this.nomWebServicesJson.put(
                "/api/Publication",
                JSON.stringify(requestParams), {
                headers: new HttpHeaders({
                    "Content-Type": "application/json",
                    "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                }),
                responseType: 'json',
                observe: 'response'
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Unable to create publication list",
                detailMessage: `Unable to create publicationList. \n\rStackTrace: CastService.createPublicationList(); \n\rError: ${error}`
            });
            return new ServiceResponse<PublicationListRecord>(false, "Unable to create publication list " + error.toString());
        }
        let serviceRecord = ServiceCollectionRecord.instance(response.body);
        serviceRecord.instantiateChildren();

        let publicationListRecord = this.nomWebServicesJson.Convertor.fromServiceCollectionRecord<PublicationListRecord>(serviceRecord);
        await DataStore.setCollection(publicationListRecord, true);

        vsPublicationTime = moment((<PublicationViewStateRecord>vs).publicationTime.get()).format("YYYY-MM-DDTHH:mm:00.000") + "Z";
        let publicationTime = moment(publicationListRecord.publicationTime.get()).format("YYYY-MM-DDTHH:mm:00.000") + "Z";
        if (vsPublicationTime == publicationTime) {
            this.castSearchResults.publicationList = DataStore.byGuid<PublicationListRecord>(publicationListRecord.guid, "");
        }

        return new ServiceResponse<PublicationListRecord>(true, DataStore.byGuid<PublicationListRecord>(publicationListRecord.guid, null));
    }

    public async getPublicationListGuid(): Promise<string> {
        let publicationListGuid: string = null;
        if (this.castSearchResults.publicationList == null) {
            let result = await this.createPublicationList(this.layoutState.ViewStateRecord, this.navigationService.currentListView.sections, this.channelSections);
            await this.navigationService.currentListView.loadPublicationList(true);
            if (result.status) {
                publicationListGuid = result.data.guid;
            }
        } else {
            publicationListGuid = this.castSearchResults.publicationList.guid;
        }
        return publicationListGuid;
    }

    public async createPublication(publication: PublicationRecord, section: string, index: number): Promise<ServiceResponse<PublicationRecord>> {
        publication.owner = this.nomWebServicesJson.UserId;
        if (!publication.relationships) {
            publication.relationships = new Relationship();
        }
        publication.relationships.setGuid(publication.linkedRecord.guid, 0);
        publication.relationships.setType("PUBLICATION", 0);
        let fields: any = this.nomWebServicesJson.Convertor.serialize(publication);
        let requestParams = {
            database: this.nomWebServicesJson.getDatabase(),
            sectionName: section,
            sectionIndex: index,
            collectionPath: this.castSearchResults.publicationList.path.fullPath,
            collectionGuid: this.castSearchResults.publicationList.guid,
            fieldsToUpdate: this.nomWebServicesJson.Convertor.toENPSFields(fields)
        };
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.patch(
                "/api/Publication",
                JSON.stringify(requestParams), {
                headers: new HttpHeaders({
                    "Content-Type": "application/json",
                    "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                }),
                responseType: 'json',
                observe: 'response'
            });
            await this.updatePublicationListModTime();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Unable to create publication",
                detailMessage: `Unable to create publication. \n\rStackTrace: CastService.createPublication(); \n\rError: ${error}`
            });
            return new ServiceResponse<PublicationRecord>(false, "Unable to create publication " + error.toString());
        }
        let record: Record = null;
        if (response.body.hasOwnProperty('Stories')) {
            let objectProperties = new ObjectProperties(response.body.Stories[0]);
            record = RecordFactory.createRecordForType(fields.Type);
            this.nomWebServicesJson.Convertor.deserialize(record, objectProperties);
            await DataStore.set(record);

        }
        if (response.status) {
            return new Promise<ServiceResponse<PublicationRecord>>(resolve => { resolve(new ServiceResponse<PublicationRecord>(true, publication)); });
        } else {
            return new Promise<ServiceResponse<PublicationRecord>>(resolve => { resolve(new ServiceResponse<PublicationRecord>(false, publication)); });
        }
    }

    public async deletePublicationRecords(publicationsProps: Array<{ publicationList: PublicationListRecord, section: string, index: number, channelSections: { [id: string]: Array<PublicationRecord> } }>): Promise<ServiceResponse<boolean>> {
        let publicationsRequestParams = [];
        for (let publicationProp of publicationsProps) {
            let publicationRecordsGuids = publicationProp.channelSections ? publicationProp.channelSections[publicationProp.section].map(rec => rec.guid).splice(publicationProp.index + 1) : []
            let params = {
                index: publicationProp.index,
                database: publicationProp.publicationList.database,
                collectionPath: publicationProp.publicationList.path.fullPath,
                collectionGuid: publicationProp.publicationList.guid,
                publicationGuid: publicationProp.channelSections ? publicationProp.channelSections[publicationProp.section][publicationProp.index].guid : '',
                publicationRecordsGuids: publicationRecordsGuids,
                fieldsToUpdate: [],
            }
            publicationsRequestParams.push(params);
        }
        let requestParams = {
            publications: publicationsRequestParams,
        }
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.delete(
                "/api/Publication",
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    body: JSON.stringify(requestParams),
                    observe: 'response'
                });
            for (let publicationProp of publicationsProps) {
                await this.updatePublicationListModTime(publicationProp.publicationList);
            }
            return new ServiceResponse<boolean>(true);
        } catch (error) {
            let msg = "Unable to delete publication.";
            this.nomWebServicesJson.showErrorModal({
                message: `${msg}`,
                detailMessage: `${msg} \n\rStackTrace: CastService.deletePublicationRecords(); \n\rError: ${error}`
            });
            return new ServiceResponse<boolean>(false, msg + error.toString());
        }
    }

    public async createPublications(publications: Array<PublicationRecord>, section: string, index: number, guids: Array<string>, publicationListGuid: string): Promise<ServiceResponse<Array<PublicationRecord>>> {
        let fields = [];
        let type: number = null;
        for (let i = 0; i < publications.length; i++) {
            publications[i].owner = this.nomWebServicesJson.UserId;
            if (!publications[i].relationships) {
                publications[i].relationships = new Relationship();
            }
            publications[i].relationships.setGuid(publications[i].linkedRecord.guid, 0);
            publications[i].relationships.setType("PUBLICATION", 0);
            let publicationFields: any = this.nomWebServicesJson.Convertor.serialize(publications[i]);
            if (!type) {
                type = publicationFields.Type;
            }
            fields.push(this.nomWebServicesJson.Convertor.toENPSFields(publicationFields));
        }

        let publicationList: PublicationListRecord = DataStore.byGuid<PublicationListRecord>(publicationListGuid, "");

        let requestParams = {
            action: 'add',
            database: this.nomWebServicesJson.getDatabase(),
            toSectionName: section,
            toSectionIndex: index,
            collectionPath: publicationList.path.fullPath,
            collectionGuid: publicationList.guid,
            fieldsToUpdateCollection: fields,
            toRecordsToUpdateGuids: guids
        };

        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.patch(
                "/api/Publication",
                JSON.stringify(requestParams), {
                headers: new HttpHeaders({
                    "Content-Type": "application/json",
                    "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                }),
                responseType: 'json',
                observe: 'response'
            });
            await this.updatePublicationListModTime(publicationList);
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Unable to create publication",
                detailMessage: `Unable to create publication. \n\rStackTrace: CastService.createPublications(); \n\rError: ${error}`
            });
            return new ServiceResponse<Array<PublicationRecord>>(false, "Unable to create publication " + error.toString());
        }
        let respRecords = response.body;

        if (response.status) {
            return new Promise<ServiceResponse<Array<PublicationRecord>>>(resolve => { resolve(new ServiceResponse<Array<PublicationRecord>>(true, publications)); });
        } else {
            return new Promise<ServiceResponse<Array<PublicationRecord>>>(resolve => { resolve(new ServiceResponse<Array<PublicationRecord>>(false, publications)); });
        }
    }

    public async updatePublicationRecords(event: { sourceSection: string, targetSection: string, sourceIndex: number, targetIndex: number }, channelSections: any) {
        let guidsFrom = [];
        let guidsTo = [];

        let toSectionIndex = event.targetIndex;

        if (event.sourceSection === event.targetSection) {
            if (event.sourceIndex > event.targetIndex) {
                let toSectionIndex = event.targetIndex;
                guidsTo = channelSections[event.targetSection].slice(event.targetIndex, event.sourceIndex + 1).map(rec => rec.guid);
            } else {
                let toSectionIndex = event.sourceIndex;
                guidsTo = channelSections[event.targetSection].slice(event.sourceIndex, event.targetIndex + 1).map(rec => rec.guid);
            }
        } else {
            guidsFrom = channelSections[event.targetSection].slice(event.sourceIndex).map(rec => rec.guid);
            guidsTo = channelSections[event.targetSection].slice(event.sourceIndex).map(rec => rec.guid);
        }
        let requestParams = {
            action: 'sort',
            database: this.nomWebServicesJson.getDatabase(),
            fromSectionName: event.sourceSection,
            fromSectionIndex: event.sourceIndex,
            fromRecordsToUpdateGuids: guidsFrom,
            toSectionName: event.targetSection,
            toSectionIndex: toSectionIndex,
            toRecordsToUpdateGuids: guidsTo,
            collectionPath: this.castSearchResults.publicationList.path.fullPath,
            collectionGuid: this.castSearchResults.publicationList.guid,
        };
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.patch(
                "/api/Publication",
                JSON.stringify(requestParams), {
                headers: new HttpHeaders({
                    "Content-Type": "application/json",
                    "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                }),
                responseType: 'json',
                observe: 'response'
            });
            await this.updatePublicationListModTime();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Unable to create publication",
                detailMessage: `Unable to update publication. \n\rStackTrace: CastService.updatePublicationRecords(); \n\rError: ${error}`
            });
            return new ServiceResponse<PublicationRecord>(false, "Unable to update publication " + error.toString());
        }
    }

    public async publishPublicationList(oldRecord: PublicationListRecord, actionId: string, channelSections: { [id: string]: Array<PublicationRecord> }): Promise<PublicationListRecord> {
        const database = oldRecord.database;
        const fullPath = oldRecord.path.fullPath;
        const guid = oldRecord.guid;
        oldRecord.channelName = (<PublicationViewStateRecord>this.viewStateService.NavigationViewStateRecord).publicationChannel.title;
        const extActionData = oldRecord.getExternalApiData(actionId, this.nomWebServicesJson.UserId, channelSections);
        const extRes: ServiceResponse<ExternalActionResponse> = await this.externalActionsService.postExternalAction(extActionData);

        if (extRes.status) {
            if (!extRes.data || !extRes.data.status || (extRes.data.status >= 300) || (extRes.data.status < 200)) {
                this.nomWebServicesJson.showErrorModal({
                    message: "Unable to publish publication list",
                    detailMessage: "Unable to publish publication list. \n\rStackTrace: CastService.publishPublicationList();"
                });
                return null;
            }

            const updateArgs = {
                "PSTPublicationListPublishedTime": moment(extActionData.timestamp).toISOString(),
                "PSTPublicationListPublishedBy": this.nomWebServicesJson.UserId,
                "Type": oldRecord.type
            }
            const updateRes = await this.basicContentService.updateBasicContent<PublicationListRecord>(database, fullPath, guid, false, updateArgs, false, null);
            if (updateRes.data) {
                return updateRes.data;
            }
        }
        return null;
    }

    public async newRevision(oldRecord: PublicationListRecord): Promise<ServiceResponse<PublicationListRecord>> {
        const database = oldRecord.database;
        const fullPath = oldRecord.path.fullPath;
        const groupId = oldRecord.path.groupId;
        const guid = oldRecord.guid;
        let fields = {
            "PSTPublicationListPublishedTime": ''
        }
        let vs = <PublicationViewStateRecord>this.viewStateService.ViewStateRecord;
        if (vs) {
            let listTitle = (<PublicationViewStateRecord>vs).publicationListTitle ? (<PublicationViewStateRecord>vs).publicationListTitle : (<PublicationViewStateRecord>vs).title;
            let title = listTitle + "-" + (<PublicationViewStateRecord>vs).publicationTime.moment.format("YYYY-MM-DD") + "-Revision" + ((<PublicationViewStateRecord>vs).publicationRevisionNumber + 2);
            fields["Title"] = title;
        }
        const res = await this.publicationService.publishPublicationList(database, fullPath, guid, groupId, true, fields);
        return res;
    }

    public async updatePublicationListModTime(publicationList?: PublicationListRecord): Promise<ServiceResponse<PublicationListRecord>> {
        let database: string = this.nomWebServicesJson.getDatabase();
        let path: string = publicationList ? publicationList.path.fullPath : this.castSearchResults.publicationList.path.fullPath;
        let guid: string = publicationList ? publicationList.guid : this.castSearchResults.publicationList.guid;
        let modTime = moment(new Date()).utc();
        let data = {
            "ModTime": modTime,
            "Type": ObjectTypes.PUBLICATIONLIST
        };
        return this.basicContentService.updateBasicContent<PublicationListRecord>(database, path, guid, false, data, false, null);
    }


    public navigateToTopic(event: INavigateEvent, dialogRef?: MatDialogRef<StFullScreenDialog>) {

        let treeControl = this.navigationService.currentListView.treeControl;
        let newIdx = -1;

        switch (event.direction) {
            case NavigationDirection.FIRST: {
                let item = treeControl.dataNodes.find((el, idx) => {
                    if (el.level == 1) {
                        newIdx = idx;
                        return true;
                    }
                })
                item = item.object?.linkedRecord;
                if (item instanceof AssignmentRecord) {
                    item = item.parentRecord as TopicRecord;
                }
                dialogRef.componentInstance.openTopic(item, item?.isMetadata);
                dialogRef.componentInstance.currentIndex = newIdx;
                dialogRef.componentInstance.indexInData = 1;
                break;
            }
            case NavigationDirection.NEXT: {
                let item = treeControl.dataNodes.find((el, idx) => {
                    if (el.level === 1 && idx > event.idx) {
                        newIdx = idx;
                        return true;
                    }
                });
                if (item) {
                    item = item.object?.linkedRecord;
                    if (item instanceof AssignmentRecord) {
                        item = item.parentRecord as TopicRecord;
                    }
                    dialogRef.componentInstance.openTopic(item, item?.isMetadata);
                    dialogRef.componentInstance.currentIndex = newIdx;
                    dialogRef.componentInstance.indexInData++;
                } else {
                    this.navigateToTopic({ direction: NavigationDirection.FIRST, idx: 0 }, dialogRef);
                }
                break;
            }
            case NavigationDirection.PREVIOUS: {
                let item = null;
                for (let i = treeControl.dataNodes.length - 1; i >= 0; i--) {
                    if (treeControl.dataNodes[i].level === 1 && i < event.idx) {
                        newIdx = i;
                        item = treeControl.dataNodes[i];
                        break;
                    }
                }
                if (item) {
                    item = item.object?.linkedRecord;
                    if (item instanceof AssignmentRecord) {
                        item = item.parentRecord as TopicRecord;
                    }
                    dialogRef.componentInstance.openTopic(item, item.isMetadata);
                    dialogRef.componentInstance.currentIndex = newIdx;
                    dialogRef.componentInstance.indexInData--;
                } else {
                    this.navigateToTopic({ direction: NavigationDirection.LAST, idx: 0 }, dialogRef);
                }
                break;
            }
            case NavigationDirection.LAST: {
                let item = null;
                for (let i = treeControl.dataNodes.length - 1; i >= 0; i--) {
                    if (treeControl.dataNodes[i].level === 1) {
                        newIdx = i;
                        item = treeControl.dataNodes[i];
                        break;
                    }
                }
                item = item.object?.linkedRecord;
                if (item instanceof AssignmentRecord) {
                    item = item.parentRecord as TopicRecord;
                }
                dialogRef.componentInstance.openTopic(item, item.isMetadata);
                dialogRef.componentInstance.currentIndex = newIdx;
                if (item.isMetadata) {
                    dialogRef.componentInstance.indexInData = treeControl.dataNodes.filter((el) => el.level == 1).length;
                } else {
                    dialogRef.componentInstance.indexInData = treeControl.dataNodes.filter((el) => el.level > 0).length;
                }
                break;
            }

        }
    }
}

DataStore.registerSearchResults("castPlaybook", new PlaybookSearchResults());
